import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import format from "date-fns/format";
import * as Yup from "yup";

// Actions
import { getCaseDetail } from "src/state/caseDetail/actions";
import { updateCaseIncident } from "src/state/incident/actions";

// Config
import { urls } from "src/config/routes";

// Components
import FormikHandler from "src/components/FormikHandler";
import WizardStepCard from "src/components/WizardStepCard";
import EvidenceStep from "./EvidenceStep";
import WhatHappenedStep from "./WhatHappenedStep";
import WhenStep from "./WhenStep";
import WhereStep from "./WhereStep";

const LAST_STEP = 4;
const emptySchema = Yup.object().shape({});

const wizardInitialValues = {
  incidentTypeId: "",
  description: "",
  dateTime: null,
  location: ""
};

const stepTitleKeys = {
  1: "caseDetailPage.requiredIncidentDetails.descriptionTitle",
  2: "caseDetailPage.requiredIncidentDetails.dateTimeTitle",
  3: "caseDetailPage.requiredIncidentDetails.locationTitle",
  4: "createCasePage.newCaseFlow.evidenceTitle"
};

// Nudges a user through incident details/evidence for a case that has none
// yet - shown on the case detail page itself (see CaseDetailPage's
// needsWizard gate), not during case creation. Per iOS, it always walks all
// 4 steps in order and never hides one. There's no per-step save: steps 1-3
// only accumulate values locally, and a single PATCH fires once when
// leaving step 3, carrying only whatever was actually filled in (not
// skipped) - if everything was skipped, no request is made at all.
const IncidentWizard = ({ caseId, incident, onComplete }) => {
  const [step, setStep] = useState(1);
  const [coord, setCoord] = useState(null);
  const [awaitingEvidenceUpload, setAwaitingEvidenceUpload] = useState(false);
  const evidenceStepRef = useRef(null);
  const { userDetails } = useSelector(s => s.user);
  const { uploadProgress, uploadErrors } = useSelector(s => s.media);
  const dispatch = useDispatch();
  const history = useHistory();

  const isEvidenceUploading =
    step === LAST_STEP &&
    !!uploadProgress &&
    Object.keys(uploadProgress).some(key => uploadProgress[key] < 100) &&
    !(
      uploadErrors && Object.keys(uploadErrors).every(key => uploadErrors[key])
    );

  // completedSections/numberOfMediaItems don't update reactively from the
  // PATCH/upload calls below, so refresh the case detail data before letting
  // the parent stop showing the wizard. isUpdate keeps data/isLoading
  // untouched while the request is in flight - without it, a failed request
  // (avoidError skips the failure dispatch, and there's no onFail/onFinish
  // here to compensate) leaves state.caseDetail stuck at
  // { data: null, isLoading: true } forever, matching SectionWrapper.jsx's
  // own getCaseDetail call for the same reason.
  const finish = () => {
    dispatch(getCaseDetail({ avoidError: true, caseId, isUpdate: true }));
    onComplete();
  };

  useEffect(() => {
    if (awaitingEvidenceUpload && !isEvidenceUploading) {
      setAwaitingEvidenceUpload(false);
      finish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingEvidenceUpload, isEvidenceUploading]);

  // Builds the single batched patch from whatever steps 1-3 actually ended
  // up with (already cleared for any step the user skipped) - only include
  // a field if it has a value, and only include incidentTypeId if it
  // actually changed from what's already stored.
  const buildIncidentPatch = values => {
    const patch = {};
    const incidentTypeId = values.incidentTypeId?.value;
    if (incidentTypeId && incidentTypeId !== incident?.incidentTypeId) {
      patch.incidentTypeId = incidentTypeId;
    }
    if (values.description) {
      patch.descriptions = [values.description];
    }
    if (values.dateTime) {
      patch.dateTime = format(new Date(values.dateTime), "yyyy-MM-dd'T'HH:mm:ss");
    }
    if (coord) {
      patch.location = {
        name: values.location,
        coordinate: { latitude: coord.lat(), longitude: coord.lng() }
      };
    }
    return patch;
  };

  const leaveStep3 = (values, setSubmitting) => {
    const patch = buildIncidentPatch(values);

    if (Object.keys(patch).length === 0) {
      if (setSubmitting) setSubmitting(false);
      setStep(4);
      return;
    }

    dispatch(
      updateCaseIncident({
        caseId,
        incident: patch,
        author: {
          ...userDetails,
          profilePictureURL: userDetails.profilePhotoURL
        },
        skipStateUpdate: true,
        onFinish: () => {
          if (setSubmitting) setSubmitting(false);
          setStep(4);
        }
      })
    );
  };

  const handleStepSubmit = (values, { setSubmitting }) => {
    if (step === 3) {
      leaveStep3(values, setSubmitting);
    } else if (step < LAST_STEP) {
      setSubmitting(false);
      setStep(step + 1);
    } else {
      const result = evidenceStepRef.current?.submitPendingFiles();
      setSubmitting(false);
      if (result === "submitted") {
        setAwaitingEvidenceUpload(true);
      } else if (result !== "invalid") {
        finish();
      }
    }
  };

  // Discards whatever was typed into the current step before moving on -
  // skip never persists anything.
  const clearStepField = (values, setFieldValue) => {
    if (step === 1) {
      setFieldValue("incidentTypeId", "");
      setFieldValue("description", "");
      return { ...values, incidentTypeId: "", description: "" };
    }
    if (step === 2) {
      setFieldValue("dateTime", null);
      return { ...values, dateTime: null };
    }
    if (step === 3) {
      setCoord(null);
      setFieldValue("location", "");
      return { ...values, location: "" };
    }
    return values;
  };

  const handleSkip = (values, setFieldValue) => () => {
    const clearedValues = clearStepField(values, setFieldValue);

    if (step === 3) {
      leaveStep3(clearedValues, null);
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step === 1) {
      history.push(urls.cases);
    } else {
      setStep(step - 1);
    }
  };

  const isStepComplete = values => {
    if (step === 1) return !!values.incidentTypeId && !!values.description;
    if (step === 2) return !!values.dateTime;
    if (step === 3) return !!values.location;
    return true;
  };

  return (
    <FormikHandler
      formik={{
        initialValues: wizardInitialValues,
        onSubmit: handleStepSubmit,
        validationSchema: emptySchema
      }}
      render={({ setFieldValue, isSubmitting, values }) => (
        <WizardStepCard
          disableSubmit={!isStepComplete(values)}
          hideSkip={step === 4}
          isSubmitting={isSubmitting || isEvidenceUploading}
          onBack={handleBack}
          onSkip={handleSkip(values, setFieldValue)}
          step={step}
          title={stepTitleKeys[step]}
        >
          {step === 1 && <WhatHappenedStep setFieldValue={setFieldValue} />}
          {step === 2 && <WhenStep />}
          {step === 3 && (
            <WhereStep
              coord={coord}
              setCoord={setCoord}
              setFieldValue={setFieldValue}
            />
          )}
          {step === 4 && <EvidenceStep ref={evidenceStepRef} caseId={caseId} />}
        </WizardStepCard>
      )}
    />
  );
};

IncidentWizard.propTypes = {
  caseId: PropTypes.string.isRequired,
  incident: PropTypes.shape({
    incidentTypeId: PropTypes.string
  }),
  onComplete: PropTypes.func.isRequired
};

IncidentWizard.defaultProps = {
  incident: null
};

export default IncidentWizard;
